import React from 'react';
import { useDrag } from 'react-dnd';
import { getCardBgClass, getCardTextColor, formatCard, getCardImage } from '../utils/cardHelpers';

export const ItemTypes = { CARD: 'card' };

function Card({
  card,
  index = 0,
  selected = false,
  highlighted = false,
  onClick,
  draggable = true,
  small = false,
  faceDown = false,
  disabled = false,
  fromBuilder = false,
  builderGroupIndex = null,
  noShadow = false,
  dragPayload = null,
  style = {}
}) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.CARD,
    item: dragPayload || { card, index, fromBuilder, builderGroupIndex },
    canDrag: draggable && !disabled && !faceDown,
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  }), [card, index, draggable, disabled, faceDown, fromBuilder, builderGroupIndex, dragPayload]);

  const baseSize = small ? 'w-8 h-12 sm:w-10 sm:h-14' : 'w-12 h-16 sm:w-16 sm:h-24';
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const imgRef = React.useRef(null);
  
  const imgSrc = getCardImage(card);

  React.useLayoutEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setImgLoaded(true);
    }
  }, [imgSrc]);

  /* ──────────────────────────────────────
     CARD BACK — dark ink-green + gold pattern
     ────────────────────────────────────── */
  if (faceDown) {
    return (
      <div
        className={`${baseSize} rounded-lg flex items-center justify-center overflow-hidden`}
        style={{
          backgroundImage: "url('/assets/mahjong_back_pattern.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          border: '2px solid rgba(212,168,67,0.7)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)',
          position: 'relative',
          ...style,
        }}
      >
        {/* Subtle inner shadow / glass lighting */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.3) 100%)',
          pointerEvents: 'none'
        }} />
      </div>
    );
  }

  /* ──────────────────────────────────────
     CARD FACE
     ────────────────────────────────────── */
  const bgClass      = getCardBgClass(card);
  const textClass    = getCardTextColor(card);
  const displayValue = formatCard(card);

  // Border color based on state
  const getBorderStyle = () => {
    if (selected)    return { border: '2px solid #d4a843', boxShadow: '0 0 0 1px rgba(212,168,67,0.4), 0 0 12px rgba(212,168,67,0.5)' };
    if (highlighted) return { border: '2px solid #27ae60', boxShadow: '0 0 12px rgba(39,174,96,0.6)' };
    return { border: '2px solid rgba(200,200,200,0.8)' };
  };

  return (
    <div
      ref={draggable ? drag : null}
      onClick={disabled ? undefined : onClick}
      className={`
        ${baseSize} ${(imgSrc && imgLoaded) ? 'bg-transparent' : bgClass} ${(imgSrc && imgLoaded) ? '' : textClass}
        rounded-lg flex items-center justify-center relative
        ${noShadow ? '' : 'shadow-md'} transition-all duration-200
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100'}
        ${onClick && !disabled ? 'cursor-pointer hover:scale-105 hover:-translate-y-2' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${draggable && !disabled ? 'draggable-card' : ''}
        card-container overflow-hidden
      `}
      style={{
        ...getBorderStyle(),
        ...style,
        cursor: isDragging ? 'grabbing' : draggable ? 'grab' : 'default',
        transform: isDragging ? 'scale(0.95) rotate(2deg)' : undefined,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      }}
    >
      {/* Fallback styling shown beneath image while loading */}
      <div className={`absolute inset-0 flex flex-col items-center justify-center z-0 overflow-hidden transition-opacity duration-200 ${(imgSrc && imgLoaded) ? 'opacity-0' : 'opacity-100'}`}>
        {card.type === 'wild' ? (
          <span className={`${small ? 'text-sm' : 'text-lg sm:text-2xl'} font-bold tracking-widest`}>Wild</span>
        ) : card.type === 'skip' ? (
          <span className={`${small ? 'text-sm' : 'text-lg sm:text-2xl'} font-bold tracking-widest`}>Skip</span>
        ) : (
          <div className="flex flex-col items-center">
            <span className={`${small ? 'text-sm' : 'text-lg sm:text-2xl'} font-bold tracking-wider`}>{displayValue.slice(0, 1)}</span>
            <span className={`${small ? 'text-sm' : 'text-lg sm:text-2xl'} font-bold tracking-wider`}>{displayValue.slice(1)}</span>
          </div>
        )}
      </div>

      {imgSrc && (
        <img
          ref={imgRef}
          src={imgSrc}
          alt={displayValue}
          onLoad={() => setImgLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover z-10 transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ pointerEvents: 'none' }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────
   CARD PLACEHOLDER
   ────────────────────────────────────── */
export function CardPlaceholder({ small = false, label = '' }) {
  const size = small ? 'w-8 h-12 sm:w-10 sm:h-14' : 'w-12 h-16 sm:w-16 sm:h-24';
  return (
    <div
      className={`${size} rounded-lg flex items-center justify-center text-center p-0.5 sm:p-1`}
      style={{
        border: '2px dashed rgba(212,168,67,0.25)',
        color: 'rgba(212,168,67,0.3)',
        fontSize: small ? '9px' : '11px',
      }}
    >
      {label}
    </div>
  );
}

/* ──────────────────────────────────────
   MINI CARD
   ────────────────────────────────────── */
export function MiniCard({ card }) {
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const imgRef = React.useRef(null);
  const bgClass      = getCardBgClass(card);
  const textClass    = getCardTextColor(card);
  const displayValue = formatCard(card);
  const imgSrc       = getCardImage(card);

  React.useLayoutEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setImgLoaded(true);
    }
  }, [imgSrc]);

  return (
    <div
      className={`w-5 h-7 ${(imgSrc && imgLoaded) ? 'bg-transparent' : bgClass} ${(imgSrc && imgLoaded) ? '' : textClass}
        flex items-center justify-center text-[10px] font-bold overflow-hidden relative
        border-y-[1px] border-r-[1px] first:border-l-[1px] border-gray-400/70 shadow-sm
        first:rounded-l last:rounded-r`}
    >
      <div className={`absolute inset-0 flex items-center justify-center z-0 transition-opacity duration-200 ${(imgSrc && imgLoaded) ? 'opacity-0' : 'opacity-100'}`}>
        {card.type === 'wild' ? 'W' : card.type === 'skip' ? 'S' : displayValue.slice(0, 1)}
      </div>
      
      {imgSrc && (
        <img 
          ref={imgRef}
          src={imgSrc} 
          alt={displayValue} 
          onLoad={() => setImgLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover z-10 transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`} 
        />
      )}
    </div>
  );
}

export default Card;
